// Certificate expiry of customers' microtech servers, as reported by
// GET /api/microtech/v1/microtech-servers/certificates.

// Outcome of the last reachability check of a server, run by the server monitor.
export interface ServerConnection {
  reachable: boolean
  checkedAt: string
  error?: string
}

export interface ServerCertificateExpiry {
  serverId: string
  name: string
  baseUrl: string
  netbird: boolean
  notAfter?: string
  source?: 'live' | 'stored'
  error?: string
  connection?: ServerConnection
}

export interface CustomerCertificateExpiry {
  customerNo: string
  servers: ServerCertificateExpiry[]
}

export type CertificateExpiryColor = 'error' | 'warning' | 'success' | 'neutral'

export interface CertificateExpirySummary {
  label: string
  color: CertificateExpiryColor
  details: string[]
  // degraded is set when at least one server's certificate was not read live:
  // it fell back to the stored enrollment certificate or could not be read.
  degraded: boolean
}

// Certificates expiring within this many days are highlighted as a warning.
export const CERTIFICATE_WARNING_DAYS = 30

const DAY_MS = 24 * 60 * 60 * 1000

const formatDay = (date: Date) =>
  date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })

const unreachableReason = (server: ServerCertificateExpiry) =>
  server.error ? `Server nicht erreichbar: ${server.error}` : 'Server nicht erreichbar'

const describeServer = (server: ServerCertificateExpiry): string => {
  const kind = server.netbird ? 'NetBird' : 'öffentlich'
  const name = server.name || server.baseUrl
  if (!server.notAfter) {
    return `${name} (${kind}): nicht lesbar (${unreachableReason(server)})`
  }
  const validUntil = `gültig bis ${formatDay(new Date(server.notAfter))}`
  if (server.source === 'live') {
    return `${name} (${kind}): ${validUntil} (vom Server gelesen)`
  }
  return `${name} (${kind}): ${validUntil} (hinterlegt, ${unreachableReason(server)})`
}

// summarizeCertificateExpiry condenses all servers of a customer to the
// earliest expiry. Servers whose certificate could not be read are only
// reported as "unbekannt" when no server has a known expiry.
export const summarizeCertificateExpiry = (
  servers: ServerCertificateExpiry[] | undefined,
  now: Date = new Date(),
): CertificateExpirySummary | null => {
  if (!servers || servers.length === 0) return null

  const details = servers.map(describeServer)
  const degraded = servers.some((s) => s.source !== 'live')
  const known = servers
    .filter((s) => s.notAfter)
    .map((s) => new Date(s.notAfter as string))
    .sort((a, b) => a.getTime() - b.getTime())
  const earliest = known[0]
  if (!earliest) {
    return { label: 'unbekannt', color: 'neutral', details, degraded }
  }

  const daysLeft = (earliest.getTime() - now.getTime()) / DAY_MS
  let color: CertificateExpiryColor = 'success'
  if (daysLeft < 0) color = 'error'
  else if (daysLeft < CERTIFICATE_WARNING_DAYS) color = 'warning'

  const label = daysLeft < 0 ? `abgelaufen ${formatDay(earliest)}` : formatDay(earliest)
  return { label, color, details, degraded }
}
